import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  TrendingUp,
  Building,
  User,
  Calendar,
  DollarSign,
  Filter,
  Download,
  Eye,
  List,
  Grid,
  BarChart3,
  Clock,
  ArrowRight
} from 'lucide-react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { reportsAPI, paymentsAPI } from '../services/api';
import { formatCurrency, formatDate, getMonthName } from '../utils/helpers';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ArrearsPage = () => {
  const [arrearsData, setArrearsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    room_id: '',
    type: '', // rent, electricity, or both
    severity: '', // critical, warning, all
    minAmount: '',
    maxAmount: ''
  });
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

  useEffect(() => {
    fetchArrearsData();
  }, []);

  useEffect(() => {
    // Set min and max amounts when data is loaded
    if (arrearsData.length > 0) {
      const amounts = arrearsData.map(a => parseFloat(a.amount));
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);
      
      setFilters(prev => ({
        ...prev,
        minAmount: prev.minAmount === '' ? min.toString() : prev.minAmount,
        maxAmount: prev.maxAmount === '' ? max.toString() : prev.maxAmount
      }));
    }
  }, [arrearsData]);

  const fetchArrearsData = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getSummary();
      setArrearsData(response.data.arrearsData || []);
    } catch (error) {
      console.error('Error fetching arrears data:', error);
      toast.error('Failed to load arrears data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    const amounts = arrearsData.map(a => parseFloat(a.amount));
    const min = amounts.length > 0 ? Math.min(...amounts) : '';
    const max = amounts.length > 0 ? Math.max(...amounts) : '';
    
    setFilters({
      room_id: '',
      type: '',
      severity: '',
      minAmount: min.toString(),
      maxAmount: max.toString()
    });
  };

  // Get unique rooms from arrears data
  const uniqueRooms = [...new Set(arrearsData.map(a => a.room_number))].sort();

  const getSeverityBadge = (daysOverdue) => {
    if (daysOverdue > 180) {
      return {
        label: 'Critical',
        className: 'bg-danger-100 text-danger-800 border border-danger-300'
      };
    } else if (daysOverdue > 60) {
      return {
        label: 'Warning',
        className: 'bg-warning-100 text-warning-800 border border-warning-300'
      };
    } else {
      return {
        label: 'Recent',
        className: 'bg-blue-100 text-blue-800 border border-blue-300'
      };
    }
  };

  const getFilteredArrears = () => {
    return arrearsData.filter(arrear => {
      // Filter by room
      if (filters.room_id && !arrear.room_number.includes(filters.room_id)) {
        return false;
      }
      
      // Filter by type
      if (filters.type && arrear.type !== filters.type) {
        return false;
      }
      
      // Filter by severity
      if (filters.severity) {
        const daysOverdue = arrear.days_overdue || 0;
        if (filters.severity === 'critical' && daysOverdue <= 180) return false;
        if (filters.severity === 'warning' && (daysOverdue <= 60 || daysOverdue > 180)) return false;
        if (filters.severity === 'recent' && daysOverdue > 60) return false;
      }
      
      // Filter by amount
      if (filters.minAmount && arrear.amount < parseFloat(filters.minAmount)) {
        return false;
      }
      if (filters.maxAmount && arrear.amount > parseFloat(filters.maxAmount)) {
        return false;
      }
      
      return true;
    });
  };

  const filteredArrears = getFilteredArrears();

  const sortedFilteredArrears = React.useMemo(() => {
    const list = (filteredArrears || []).slice();
    if (!sortConfig.key) return list;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'room':
          return String(a.room_number || '').localeCompare(String(b.room_number || ''), undefined, { numeric: true, sensitivity: 'base' }) * dir;
        case 'tenant':
          return String(a.tenant_name || '').localeCompare(String(b.tenant_name || ''), undefined, { sensitivity: 'base' }) * dir;
        case 'type':
          return String(a.type || '').localeCompare(String(b.type || ''), undefined, { sensitivity: 'base' }) * dir;
        case 'amount':
          return (Number(a.amount || 0) - Number(b.amount || 0)) * dir;
        case 'days_overdue':
          return ((Number(a.days_overdue) || 0) - (Number(b.days_overdue) || 0)) * dir;
        case 'due_date':
          return ((a.due_date ? new Date(a.due_date).getTime() : 0) - (b.due_date ? new Date(b.due_date).getTime() : 0)) * dir;
        case 'status': {
          const la = getSeverityBadge(a.days_overdue).label || '';
          const lb = getSeverityBadge(b.days_overdue).label || '';
          return String(la).localeCompare(String(lb), undefined, { sensitivity: 'base' }) * dir;
        }
        default:
          return 0;
      }
    });
  }, [filteredArrears, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline-block ml-2" />
    ) : (
      <ChevronDown className="w-3 h-3 inline-block ml-2" />
    );
  };

  const totalArrears = filteredArrears.reduce((sum, arrear) => sum + parseFloat(arrear.amount || 0), 0);
  const criticalCount = filteredArrears.filter(a => a.days_overdue > 180).length;
  const warningCount = filteredArrears.filter(a => a.days_overdue > 60 && a.days_overdue <= 180).length;

  const formatPDFDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName} ${day}-${month}-${year}`;
  };

  const handleDownloadPDF = () => {
    if (filteredArrears.length === 0) {
      toast.error('No arrears to download');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Arrears Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: white;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #ef4444;
            padding-bottom: 15px;
          }
          .header h1 {
            font-size: 28px;
            margin-bottom: 8px;
            color: #dc2626;
          }
          .header p {
            font-size: 13px;
            color: #555;
          }
          .summary {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 25px;
            padding: 15px;
            background: #fef2f2;
            border-radius: 6px;
            border: 1px solid #fecaca;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            font-size: 11px;
            color: #991b1b;
            margin-bottom: 5px;
            font-weight: 600;
          }
          .summary-value {
            font-size: 18px;
            font-weight: bold;
            color: #dc2626;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 11px;
          }
          thead {
            background: #dc2626;
            color: white;
          }
          th {
            padding: 10px 6px;
            text-align: left;
            font-weight: 600;
            font-size: 11px;
          }
          th.amount-header {
            text-align: center;
          }
          td {
            padding: 8px 6px;
            border-bottom: 1px solid #e0e0e0;
          }
          tbody tr:nth-child(even) {
            background: #fef2f2;
          }
          tbody tr:hover {
            background: #fee2e2;
          }
          .amount {
            font-weight: 600;
            text-align: center;
            color: #dc2626;
          }
          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: 600;
          }
          .badge-critical {
            background: #fee2e2;
            color: #991b1b;
            border: 1px solid #fecaca;
          }
          .badge-warning {
            background: #fef3c7;
            color: #92400e;
            border: 1px solid #fde68a;
          }
          .badge-recent {
            background: #dbeafe;
            color: #1e40af;
            border: 1px solid #bfdbfe;
          }
          .badge-rent {
            background: #dbeafe;
            color: #1e40af;
          }
          .badge-electricity {
            background: #fef3c7;
            color: #92400e;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: #dc2626;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .print-button:hover {
            background: #b91c1c;
          }
          @media print {
            .print-button {
              display: none;
            }
            body {
              padding: 0;
            }
            @page {
              size: A4;
              margin: 15mm;
            }
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
        
        <div class="header">
          <h1>⚠️ ARREARS REPORT</h1>
          <p>Outstanding Payments Summary</p>
          <p style="font-size: 11px; margin-top: 5px;">Generated on ${formatPDFDate(new Date().toISOString())}</p>
        </div>

        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Arrears</div>
            <div class="summary-value">${formatCurrency(totalArrears)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Affected Rooms</div>
            <div class="summary-value">${filteredArrears.length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Critical Cases</div>
            <div class="summary-value">${criticalCount}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Warning Cases</div>
            <div class="summary-value">${warningCount}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Room</th>
              <th>Tenant</th>
              <th>Type</th>
              <th class="amount-header">Amount</th>
              <th>Days Overdue</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredArrears.map(arrear => {
              const severity = getSeverityBadge(arrear.days_overdue);
              return `
                <tr>
                  <td><strong>${arrear.room_number}</strong></td>
                  <td>${arrear.tenant_name || 'Vacant'}</td>
                  <td>
                    <span class="badge badge-${arrear.type}">
                      ${arrear.type?.charAt(0).toUpperCase() + arrear.type?.slice(1)}
                    </span>
                  </td>
                  <td class="amount">${formatCurrency(arrear.amount)}</td>
                  <td>${arrear.days_overdue} days</td>
                  <td>
                    <span class="badge badge-${severity.label.toLowerCase()}">
                      ${severity.label}
                    </span>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Arrears Report - Tenant Bills Management System</p>
          <p>⚠️ Immediate action required for critical cases (>180 days overdue)</p>
        </div>
      </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    setTimeout(() => {
      newWindow.document.title = 'Arrears Report';
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Arrears Management</h1>
          <p className="text-secondary-500 dark:text-secondary-400">Track and manage outstanding payments</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-secondary-100 dark:bg-secondary-800 rounded-xl p-1 gap-1">
            {[
              { mode: 'list', icon: List, label: 'List' },
              { mode: 'grid', icon: Grid, label: 'Grid' },
              { mode: 'chart', icon: BarChart3, label: 'Chart' },
            ].map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  viewMode === mode
                    ? 'bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 shadow-sm'
                    : 'text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:from-red-700 hover:to-rose-800 transition-all"
            title="Download Arrears Report"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download PDF</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center flex-shrink-0 shadow-md">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Arrears</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{formatCurrency(totalArrears)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-md">
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Affected Rooms</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">{filteredArrears.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0 shadow-md">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Critical Cases</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{criticalCount}</p>
              <p className="text-xs text-secondary-400">&gt;180 days overdue</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Warning Cases</p>
              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{warningCount}</p>
              <p className="text-xs text-secondary-400">60–180 days overdue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-secondary-400" />
          <span className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Room</label>
            <select
              name="room_id"
              value={filters.room_id}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">All Rooms</option>
              {uniqueRooms.map(room => (
                <option key={room} value={room}>{room}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Type</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">All Types</option>
              <option value="rent">Rent Only</option>
              <option value="electricity">Electricity Only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Severity</label>
            <select
              name="severity"
              value={filters.severity}
              onChange={handleFilterChange}
              className="form-input"
            >
              <option value="">All Severity</option>
              <option value="critical">Critical (&gt;180 days)</option>
              <option value="warning">Warning (60-180 days)</option>
              <option value="recent">Recent (&lt;60 days)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Min Amount</label>
            <input
              type="number"
              name="minAmount"
              value={filters.minAmount}
              onChange={handleFilterChange}
              placeholder="Min Amount"
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Max Amount</label>
            <input
              type="number"
              name="maxAmount"
              value={filters.maxAmount}
              onChange={handleFilterChange}
              placeholder="Max Amount"
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 rounded-xl border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 text-sm font-medium hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Content Display */}
      {filteredArrears.length > 0 ? (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              {/* Table header bar */}
              <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-rose-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-white font-semibold text-sm">Outstanding Arrears</h3>
                </div>
                <span className="text-red-100 text-xs font-medium">{filteredArrears.length} record{filteredArrears.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-secondary-100 dark:border-secondary-700">
                      <th onClick={() => setSortConfig(prev => ({ key: 'room', direction: prev.key === 'room' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room {renderSortIcon('room')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'tenant', direction: prev.key === 'tenant' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Tenant {renderSortIcon('tenant')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'type', direction: prev.key === 'type' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Type {renderSortIcon('type')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'amount', direction: prev.key === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Amount {renderSortIcon('amount')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'days_overdue', direction: prev.key === 'days_overdue' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Days Overdue {renderSortIcon('days_overdue')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'due_date', direction: prev.key === 'due_date' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Due Period {renderSortIcon('due_date')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'status', direction: prev.key === 'status' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status {renderSortIcon('status')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                    {sortedFilteredArrears.map((arrear, index) => {
                      const severity = getSeverityBadge(arrear.days_overdue);
                      return (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors group"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center flex-shrink-0">
                                <Building className="w-3.5 h-3.5 text-white" />
                              </div>
                              <span className="font-semibold text-secondary-900 dark:text-secondary-100 text-sm">{arrear.room_number}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                              <span className="text-sm text-secondary-700 dark:text-secondary-300">{arrear.tenant_name || 'Vacant'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              arrear.type === 'rent'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            }`}>
                              {arrear.type?.charAt(0).toUpperCase() + arrear.type?.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-bold text-red-600 dark:text-red-400 text-sm">{formatCurrency(arrear.amount)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-secondary-400" />
                              <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">{arrear.days_overdue} days</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary-500 dark:text-secondary-400">{arrear.due_date}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${severity.className}`}>
                              {severity.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => navigate('/payments')}
                              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-600 to-rose-700 text-white text-xs font-semibold hover:shadow-md"
                              title="Record Payment"
                            >
                              Pay Now
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sortedFilteredArrears.map((arrear, index) => {
                const severity = getSeverityBadge(arrear.days_overdue);
                const isCritical = arrear.days_overdue > 180;
                const isWarning = arrear.days_overdue > 60 && arrear.days_overdue <= 180;
                const gradientClass = isCritical
                  ? 'from-red-600 to-rose-700'
                  : isWarning
                  ? 'from-amber-500 to-orange-600'
                  : 'from-blue-500 to-indigo-600';

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Card header gradient */}
                    <div className={`px-5 py-4 bg-gradient-to-r ${gradientClass} flex items-center justify-between`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-lg leading-tight">{formatCurrency(arrear.amount)}</p>
                          <p className="text-white/70 text-xs">{arrear.days_overdue} days overdue</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/30`}>
                        {severity.label}
                      </span>
                    </div>

                    {/* Card body */}
                    <div className="px-5 py-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Building className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                        <span className="text-secondary-500 dark:text-secondary-400">Room</span>
                        <span className="font-semibold text-secondary-900 dark:text-secondary-100 ml-auto">{arrear.room_number}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                        <span className="text-secondary-500 dark:text-secondary-400">Tenant</span>
                        <span className="text-secondary-700 dark:text-secondary-300 ml-auto truncate max-w-[120px]">{arrear.tenant_name || 'Vacant'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                        <span className="text-secondary-500 dark:text-secondary-400">Type</span>
                        <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          arrear.type === 'rent'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {arrear.type?.charAt(0).toUpperCase() + arrear.type?.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                        <span className="text-secondary-500 dark:text-secondary-400">Due</span>
                        <span className="text-secondary-700 dark:text-secondary-300 ml-auto">{arrear.due_date}</span>
                      </div>
                    </div>

                    {/* Card footer */}
                    <div className="px-5 pb-4">
                      <button
                        onClick={() => navigate('/payments')}
                        className={`w-full py-2 rounded-xl bg-gradient-to-r ${gradientClass} text-white text-sm font-semibold flex items-center justify-center gap-2 hover:shadow-md transition-shadow`}
                      >
                        Record Payment
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Chart View */}
          {viewMode === 'chart' && (
            <div className="space-y-6">
              {/* KPI row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Arrears</p>
                    <p className="text-xl font-bold text-red-600 dark:text-red-400">{formatCurrency(totalArrears)}</p>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Critical Cases</p>
                    <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">{criticalCount}</p>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Avg Days Overdue</p>
                    <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                      {Math.round(filteredArrears.reduce((sum, a) => sum + (a.days_overdue || 0), 0) / filteredArrears.length)} days
                    </p>
                  </div>
                </div>
              </div>

              {/* Arrears by Room */}
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-rose-700 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-white font-semibold text-sm">Arrears by Room</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={filteredArrears.map(a => ({
                        room: a.room_number,
                        amount: parseFloat(a.amount),
                        days: a.days_overdue
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="room" tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'amount') return formatCurrency(value);
                          return `${value} days`;
                        }}
                        contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Bar dataKey="amount" fill="#ef4444" name="Arrears Amount" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Arrears by Type */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-rose-700 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Arrears by Type</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(
                            filteredArrears.reduce((acc, arrear) => {
                              const type = arrear.type?.charAt(0).toUpperCase() + arrear.type?.slice(1) || 'Unknown';
                              if (!acc[type]) acc[type] = 0;
                              acc[type] += parseFloat(arrear.amount);
                              return acc;
                            }, {})
                          ).map(([name, value]) => ({ name, value }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {Object.keys(
                            filteredArrears.reduce((acc, arrear) => {
                              const type = arrear.type?.charAt(0).toUpperCase() + arrear.type?.slice(1) || 'Unknown';
                              acc[type] = true;
                              return acc;
                            }, {})
                          ).map((name, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Severity Distribution */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-rose-700 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Severity Distribution</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          {
                            severity: 'Critical',
                            count: criticalCount,
                            amount: filteredArrears.filter(a => a.days_overdue > 180).reduce((sum, a) => sum + parseFloat(a.amount), 0)
                          },
                          {
                            severity: 'Warning',
                            count: warningCount,
                            amount: filteredArrears.filter(a => a.days_overdue > 60 && a.days_overdue <= 180).reduce((sum, a) => sum + parseFloat(a.amount), 0)
                          },
                          {
                            severity: 'Recent',
                            count: filteredArrears.filter(a => a.days_overdue <= 60).length,
                            amount: filteredArrears.filter(a => a.days_overdue <= 60).reduce((sum, a) => sum + parseFloat(a.amount), 0)
                          }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="severity" tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'amount') return formatCurrency(value);
                            return value;
                          }}
                          contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="count" fill="#8b5cf6" name="Count" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="amount" fill="#ef4444" name="Amount" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Days Overdue by Room */}
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-rose-700 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-white font-semibold text-sm">Days Overdue by Room</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={filteredArrears.map(a => ({
                        room: a.room_number,
                        days: a.days_overdue
                      }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(v) => `${v}d`} />
                      <YAxis dataKey="room" type="category" width={80} tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <Tooltip
                        formatter={(value) => `${value} days`}
                        contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Bar dataKey="days" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700">
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">No arrears found</h3>
            <p className="text-secondary-500 dark:text-secondary-400 mb-6">
              {arrearsData.length === 0
                ? 'Great! All payments are up to date.'
                : 'No arrears match your current filters.'}
            </p>
            {arrearsData.length > 0 && filteredArrears.length === 0 && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-rose-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:from-red-700 hover:to-rose-800 transition-all"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArrearsPage;
